import { useParams } from 'react-router';
import { usePageTitle } from '@/hooks/use-page-title';
import { Card } from '@/components/ui/card';
import { Eye, X } from 'lucide-react';
import { toast } from 'sonner';
import { getCapabilityColors } from '@/lib/capability-colors';
import {
    Table,
    TableHeader,
    TableBody,
    TableHead,
    TableRow,
    TableCell,
} from '@/components/ui/table';
import { VisibilityBadge } from '@/components/ui/visibility-badge';
import {
    useAppCapabilityVisibility,
    useUpdateAppCapabilityVisibility,
    useDeleteAppCapabilityVisibility,
} from '@/hooks/use-app-capability-visibility';
import type { CapabilityVisibility, ApplicationCapabilityVisibilityRow } from '@/types/capability-types';

const VISIBILITY_RANK: Record<string, number> = {
    global: 0,
    organization: 1,
    application: 2,
    account: 3,
    user: 4,
};

const ALL_VISIBILITY_OPTIONS: { value: CapabilityVisibility; label: string }[] = [
    { value: 'global', label: 'Global' },
    { value: 'organization', label: 'Organization' },
    { value: 'application', label: 'Application' },
    { value: 'account', label: 'Account' },
    { value: 'user', label: 'User' },
];

function getRestrictiveOptions(capabilityVisibility: CapabilityVisibility) {
    const capRank = VISIBILITY_RANK[capabilityVisibility] ?? 0;
    return ALL_VISIBILITY_OPTIONS.filter((opt) => VISIBILITY_RANK[opt.value] >= capRank);
}

function OverrideSelect({
    row,
    onUpdate,
    onClear,
}: {
    row: ApplicationCapabilityVisibilityRow;
    onUpdate: (capabilityId: string, visibility: CapabilityVisibility) => void;
    onClear: (capabilityId: string) => void;
}) {
    const options = getRestrictiveOptions(row.capability_visibility);
    const currentValue = row.app_visibility_override ?? '';

    return (
        <div className="flex items-center gap-1">
            <select
                value={currentValue}
                onChange={(e) => {
                    const val = e.target.value as CapabilityVisibility;
                    if (val) {
                        onUpdate(row.capability_id, val);
                    }
                }}
                className="h-7 rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-700 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            >
                <option value="">No override</option>
                {options.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                        {opt.label}
                    </option>
                ))}
            </select>
            {row.app_visibility_override && (
                <button
                    type="button"
                    onClick={() => onClear(row.capability_id)}
                    className="rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                    title="Clear override"
                >
                    <X size={12} />
                </button>
            )}
        </div>
    );
}

function CapabilityVisibilityTable({ appKey }: { appKey: string }) {
    const { data: rows, isLoading, isError } = useAppCapabilityVisibility(appKey);
    const updateMutation = useUpdateAppCapabilityVisibility(appKey);
    const deleteMutation = useDeleteAppCapabilityVisibility(appKey);

    function handleUpdate(capabilityId: string, visibility: CapabilityVisibility) {
        updateMutation.mutate([{ capability_id: capabilityId, visibility }], {
            onError: () => toast.error('Failed to update visibility override'),
        });
    }

    function handleClear(capabilityId: string) {
        deleteMutation.mutate(capabilityId, {
            onError: () => toast.error('Failed to remove visibility override'),
        });
    }

    if (isLoading) {
        return <div className="py-4 text-center text-sm text-slate-400">Loading capabilities...</div>;
    }

    if (isError) {
        return (
            <div className="py-6 text-center text-sm text-slate-400">
                Failed to load capability visibility data.
            </div>
        );
    }

    if (!rows || rows.length === 0) {
        return (
            <div className="py-6 text-center text-sm text-slate-400">
                No capabilities found in this application.
            </div>
        );
    }

    return (
        <Table>
            <TableHeader>
                <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Capability Visibility</TableHead>
                    <TableHead>App Override</TableHead>
                    <TableHead>Effective</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {rows.map((row) => {
                    const colors = getCapabilityColors(row.capability_type);
                    const Icon = colors.icon;
                    return (
                        <TableRow key={row.capability_id}>
                            <TableCell>
                                <div className="flex items-center gap-2">
                                    <div className={`flex h-6 w-6 shrink-0 items-center justify-center rounded ${colors.bgLight}`}>
                                        <Icon size={13} className={colors.text} />
                                    </div>
                                    <span className="font-medium text-slate-900">
                                        {row.capability_display_name ?? row.capability_name}
                                    </span>
                                </div>
                            </TableCell>
                            <TableCell>
                                <span className="text-xs capitalize text-slate-500">
                                    {row.capability_type.replace(/_/g, ' ')}
                                </span>
                            </TableCell>
                            <TableCell>
                                <VisibilityBadge value={row.capability_visibility} />
                            </TableCell>
                            <TableCell>
                                <OverrideSelect
                                    row={row}
                                    onUpdate={handleUpdate}
                                    onClear={handleClear}
                                />
                            </TableCell>
                            <TableCell>
                                <VisibilityBadge value={row.effective_visibility} />
                            </TableCell>
                        </TableRow>
                    );
                })}
            </TableBody>
        </Table>
    );
}

export default function ApplicationVisibilityPage() {
    const { appKey } = useParams();
    usePageTitle(`Visibility - ${appKey}`);

    if (!appKey) {
        return <div className="p-6 text-sm text-slate-400">No application selected.</div>;
    }

    return (
        <div className="space-y-6 p-6">
            <div className="mx-auto w-full max-w-5xl">
                <div className="mb-4 flex items-center gap-2">
                    <Eye size={16} className="text-slate-500" />
                    <h2 className="font-semibold text-slate-900">Capability Visibility</h2>
                </div>
                <p className="mb-4 text-sm text-slate-500">
                    Manage visibility overrides for capabilities within this application.
                    An override restricts a capability's visibility to a narrower scope than its intrinsic setting.
                </p>
                <Card className="overflow-hidden border border-[hsl(var(--border))]">
                    <CapabilityVisibilityTable appKey={appKey} />
                </Card>
            </div>
        </div>
    );
}
